// Typed knowledge base for Dominique Stroobant.
// Single source of truth for every visualization in the app.

export interface Person {
  id: string;
  name: string;
  role: string;
  kind: 'self' | 'collaborator' | 'family' | 'institution' | 'group' | 'scholar';
  detail?: string;
}

export interface Edge {
  from: string;
  to: string;
  kind: 'family' | 'collaboration' | 'friendship' | 'membership' | 'representation' | 'subject';
  label?: string;
}

export interface Place {
  id: string;
  name: string;
  country: string;
  coords: [number, number]; // [lat, lon]
  role: 'birth' | 'education' | 'residence' | 'quarry' | 'studio' | 'commission' | 'exhibition' | 'family' | 'publisher';
  yearsAssociated?: number;
  note?: string;
}

export interface RoutePoint {
  place: string;
  coords: [number, number];
  caption: string;
  date?: string;
}

export interface TimelineEvent {
  year: number;
  endYear?: number;
  title: string;
  lane: 'life' | 'works' | 'exhibitions';
  hero?: boolean;
  detail?: string;
}

export interface Casualty {
  country: string;
  count: number;
  flagEmoji?: string;
}

export interface ExposureStep {
  label: string;
  seconds: number;
  iconHint: string;
  detail?: string;
}

export interface DecadeBucket {
  label: string;
  startYear: number;
  solo: number;
  group: number;
  highlights: string[];
}

export interface Quote {
  text: string;
  source: string;
  emphasis?: string;
}

export const IDENTITY = {
  name: 'Dominique Stroobant',
  born: '16 March 1947',
  birthPlace: 'Antwerp, Belgium',
  residence: 'Miseglia, Carrara, Italy (since 1970)',
  disciplines: ['Sculptor', 'Photographer', 'Stonemason', 'Graphic artist'],
  ageAsOf2026: 79,
};

export const PLACES: Place[] = [
  { id: 'antwerp', name: 'Antwerp', country: 'Belgium', coords: [51.2194, 4.4025], role: 'birth', note: 'Birthplace, 1947' },
  { id: 'brussels', name: 'Brussels', country: 'Belgium', coords: [50.8503, 4.3517], role: 'education', note: 'École Supérieure des Arts Saint-Luc, from 1966' },
  { id: 'miseglia', name: 'Miseglia', country: 'Italy', coords: [44.0835, 10.1156], role: 'residence', yearsAssociated: 56, note: 'Residence & studio since 1970 — 240 m elev., pop. 449' },
  { id: 'fantiscritti', name: 'Canale di Fantiscritti', country: 'Italy', coords: [44.0920, 10.1310], role: 'quarry', note: 'Marble quarry, Roman-era origin' },
  { id: 'canalgrande', name: 'Canalgrande', country: 'Italy', coords: [44.0940, 10.1220], role: 'quarry', note: 'Marble quarry' },
  { id: 'marina-di-carrara', name: 'Marina di Carrara', country: 'Italy', coords: [44.0367, 10.0394], role: 'studio', note: 'Stonemason workshop for Kontinuität, 1984–86' },
  { id: 'scarraciana', name: 'Scarraciana quarry, near Tempio Pausania', country: 'Italy (Sardinia)', coords: [40.9, 9.1], role: 'quarry', note: 'Granite source for Kontinuität (Rosa Sardo)' },
  { id: 'frankfurt', name: 'Taunusanlage, Frankfurt', country: 'Germany', coords: [50.1133, 8.6686], role: 'commission', note: 'Kontinuität installed 1986' },
  { id: 'marcinelle', name: 'Bois du Cazier, Marcinelle', country: 'Belgium', coords: [50.4054, 4.4486], role: 'commission', note: 'Memorial sculpture, 1988' },
  { id: 'venice', name: 'Venice', country: 'Italy', coords: [45.4408, 12.3155], role: 'exhibition', note: 'Biennale 1976/1978, Artempo 2012' },
  { id: 'cape-town', name: 'Cape Town', country: 'South Africa', coords: [-33.9249, 18.4241], role: 'family', note: "Wife Mona Johnson's home" },
  { id: 'gorinchem', name: 'Gorinchem', country: 'Netherlands', coords: [51.8362, 4.9744], role: 'publisher', note: 'Kunstcentrum Badhuis, 1980 monograph' },
  { id: 'villeurbanne', name: 'Villeurbanne', country: 'France', coords: [45.7665, 4.8795], role: 'exhibition', note: 'L\'art et le temps, 1985–86' },
  { id: 'houston', name: 'Houston', country: 'USA', coords: [29.7604, -95.3698], role: 'exhibition', note: 'Pinhole Photography, 1990' },
  { id: 'siena', name: 'Siena', country: 'Italy', coords: [43.3188, 11.3308], role: 'exhibition', note: 'Senza Obiettivo, 2002' },
  { id: 'florence', name: 'Florence', country: 'Italy', coords: [43.7696, 11.2558], role: 'exhibition', note: 'Museo Galileo round table, 2013' },
  { id: 'brilschans', name: 'Brialmontpark / Brilschans, Berchem (Antwerp)', country: 'Belgium', coords: [51.1962, 4.4194], role: 'commission', note: '1968 — De kinderen noemen het een olifant — from the keystone of the demolished Berchemsepoort' },
  { id: 'univ-barcelona', name: 'Universitat de Barcelona — Facultat de Ciències Empresarials', country: 'Spain', coords: [41.3768, 2.1175], role: 'commission', note: '2001 — Ferrer i Guàrdia bas-relief, with 7 Carrara students, FAI' },
];

export interface PoliticalStone {
  id: string;
  year: number;
  title: string;
  location: string;
  country: string;
  coords: [number, number];
  whoFor: string;
  material: string;
  detail: string;
}

export const POLITICAL_STONES: PoliticalStone[] = [
  {
    id: 'brilschans-1968',
    year: 1968,
    title: 'De kinderen noemen het een olifant',
    location: 'Brialmontpark / Brilschans, Berchem',
    country: 'Belgium',
    coords: [51.1962, 4.4194],
    whoFor: 'A demolished military gate — children renamed it an elephant.',
    material: 'Keystone / lintel of the Berchemsepoort',
    detail: 'His earliest documented public sculpture. Age 21. Made while still a Saint-Luc student.',
  },
  {
    id: 'marcinelle-1988',
    year: 1988,
    title: 'Memorial sculpture',
    location: 'Bois du Cazier, Marcinelle (Charleroi)',
    country: 'Belgium',
    coords: [50.4054, 4.4486],
    whoFor: 'The 262 miners killed in the 8 August 1956 disaster — 136 of them Italian migrants.',
    material: 'Stone, carved in situ at the coal-mine site',
    detail: 'UNESCO World Heritage Site since 2012.',
  },
  {
    id: 'ferrer-2001',
    year: 2001,
    title: 'Ferrer i Guàrdia bas-relief',
    location: 'Facultat de Ciències Empresarials, Universitat de Barcelona',
    country: 'Spain',
    coords: [41.3768, 2.1175],
    whoFor: 'Francesc Ferrer i Guàrdia — anarchist pedagogue executed at Montjuïc in 1909.',
    material: 'Marble bas-relief',
    detail: 'Reproduces a drawing by Flavio Costantini. Carved by 7 Carrara students directed by DS, on commission from the Federazione Anarchica Italiana. Installed 16 October 2001.',
  },
];

export interface AnarchistEvent {
  year: number | string;
  label: string;
  emphasis?: boolean;
}

export const ANARCHIST_CARRARA: AnarchistEvent[] = [
  { year: 1894, label: 'Uprising against conscription; 11 killed at Dogali' },
  { year: 1900, label: 'Gaetano Bresci kills King Umberto I' },
  { year: 1911, label: '6½-hour workday for quarrymen', emphasis: true },
  { year: 1944, label: 'Women\'s rebellion during the Resistance' },
  { year: 1945, label: 'FAI founded, 1st Anarchist Congress', emphasis: true },
  { year: 1968, label: 'IAF-IFA founded; ~90% anarchist in region', emphasis: true },
  { year: 1970, label: 'Stroobant arrives in Miseglia', emphasis: true },
  { year: 2001, label: 'Ferrer monument for Barcelona', emphasis: true },
];

export const KONTINUITAT_ROUTE: RoutePoint[] = [
  { place: 'Scarraciana quarry, Sardinia', coords: [40.9, 9.1], caption: '180 t block extracted', date: '20 Oct 1983' },
  { place: 'Marina di Carrara', coords: [44.0367, 10.0394], caption: 'Carving workshop', date: '20 Feb 1984' },
  { place: 'Rotterdam', coords: [51.9244, 4.4777], caption: 'Arrived by sea', date: '2 Sept 1986' },
  { place: 'Frankfurt am Main', coords: [50.1133, 8.6686], caption: 'Installed at Deutsche Bank', date: '7 Sept 1986' },
];

/* Approximate sea-and-river waypoints — drawn as a continuous polyline so the
   path bends through real waterways (Tyrrhenian, Med, Gibraltar, Atlantic,
   English Channel, North Sea, Rhine, Main) rather than cutting across land. */
export const KONTINUITAT_PATH: Array<[number, number]> = [
  // ── Segment 1: Sardinia → Marina di Carrara (Tyrrhenian Sea) ──
  [40.9, 9.1],         // Scarraciana quarry, interior Sardinia
  [40.92, 9.50],       // Olbia — loading port
  [42.00, 9.65],       // east of Corsica
  [43.20, 9.85],       // approaching Liguria
  [44.0367, 10.0394],  // Marina di Carrara

  // ── Segment 2: Marina di Carrara → Rotterdam (Med + Atlantic + Channel + N Sea) ──
  [43.00, 9.40],       // south, off Liguria
  [41.50, 9.10],       // approaching Bonifacio Strait
  [39.20, 8.50],       // south of Sardinia
  [37.80, 5.50],       // open Mediterranean
  [36.80, 0.00],       // off Algerian coast
  [36.30, -3.20],      // approaching Andalusia
  [35.95, -5.35],      // Strait of Gibraltar
  [36.30, -7.50],      // Gulf of Cádiz
  [38.50, -9.80],      // west of Lisbon
  [42.00, -10.30],     // off Galicia
  [45.20, -7.00],      // Bay of Biscay
  [48.20, -5.40],      // south of Cornwall
  [50.30, -1.80],      // English Channel
  [51.10, 1.60],       // Strait of Dover
  [51.65, 3.30],       // approaching Dutch coast
  [51.9244, 4.4777],   // Rotterdam

  // ── Segment 3: Rotterdam → Frankfurt (up the Rhine, then up the Main) ──
  [51.83, 5.85],       // Rhine — Nijmegen
  [51.50, 6.40],       // German border
  [51.20, 7.00],       // Duisburg
  [50.85, 7.10],       // Bonn
  [50.36, 7.60],       // Koblenz
  [50.00, 8.27],       // Mainz — Rhine-Main confluence
  [50.02, 8.50],       // heading east on the Main
  [50.1133, 8.6686],   // Frankfurt am Main
];

export const TIMELINE: TimelineEvent[] = [
  { year: 1947, title: 'Born in Antwerp, Belgium', lane: 'life', hero: true, detail: '16 March' },
  { year: 1960, title: '"I knew at 13 I would be a stonemason"', lane: 'life' },
  { year: 1966, endYear: 1970, title: 'École Saint-Luc, Brussels — pupil of Stan Hensen & Jean Guiraud', lane: 'life' },
  { year: 1969, title: 'Meets Mona Johnson', lane: 'life' },
  { year: 1970, title: 'State grant to Carrara — settles in Miseglia', lane: 'life', hero: true },
  { year: 1971, title: 'First sculpture enters Middelheim Museum (inv. MID.B.417)', lane: 'works' },
  { year: 1972, title: 'Founds Floating Stones Group', lane: 'works', detail: 'with Kenneth Davis & Philippe Toussaint' },
  { year: 1972, title: 'First lithophonic experiments', lane: 'works' },
  { year: 1972, title: 'First solo show — Galleria Penta, Antwerp', lane: 'exhibitions' },
  { year: 1972, title: 'Sculpture symposium — Lindabrunn, Austria', lane: 'works' },
  { year: 1972, title: 'Son Ish-maël born', lane: 'life' },
  { year: 1973, title: 'Marries Mona Johnson; meets Jef Verheyen', lane: 'life' },
  { year: 1975, title: '"World\'s biggest granite stamp" for Wout Vercammen', lane: 'works' },
  { year: 1975, title: 'Founds Laboratorio STYLITHOS, via Fantiscritti 11', lane: 'works' },
  { year: 1976, title: '"Behind those stones" — film with Dirk Van den Eynde, Karl Prantl, Lega Cavatori di Torano', lane: 'works' },
  { year: 1976, title: 'Begins working with Fratelli Biselli', lane: 'works' },
  { year: 1976, title: 'Behind these Stones — Venice Biennale (37th)', lane: 'exhibitions' },
  { year: 1977, title: 'First heliograph: pinhole camera tracks the sun', lane: 'works', hero: true },
  { year: 1977, title: 'Wins Eugène Baie award (Province of Antwerp)', lane: 'works' },
  { year: 1978, title: 'Sculpture symposium — Suwako, Japan', lane: 'works' },
  { year: 1978, title: 'Daughter Mascha born', lane: 'life' },
  { year: 1979, title: 'Eliofanografie — first dedicated pinhole solo (Fucecchio)', lane: 'exhibitions' },
  { year: 1979, endYear: 1981, title: 'Steenklok V', lane: 'works' },
  { year: 1980, title: 'Dag in dag uit monograph (Kunstcentrum Badhuis)', lane: 'works' },
  { year: 1980, title: 'Hole-o-graphy (with Terrence Dinnan) — ICC Antwerp', lane: 'exhibitions' },
  { year: 1982, title: 'Camera Oscura published with Hendel Teicher', lane: 'works' },
  { year: 1982, title: '10-metre-long pinhole emulsions, Geneva', lane: 'works' },
  { year: 1983, endYear: 1986, title: 'Carves Kontinuität with Max Bill', lane: 'works', hero: true },
  { year: 1985, title: "L'art et le temps — Villeurbanne", lane: 'exhibitions' },
  { year: 1988, title: 'Marcinelle memorial at Bois du Cazier', lane: 'works', hero: true },
  { year: 1990, title: 'Pinhole Photography — Houston', lane: 'exhibitions' },
  { year: 1991, endYear: 1992, title: 'Max Bill commissions executed with El Rayes', lane: 'works' },
  { year: 1994, title: 'Meets Jean Gimpel & Tiziano Mannoni', lane: 'life' },
  { year: 1998, title: 'Death of his wife Mona', lane: 'life' },
  { year: 1999, endYear: 2004, title: 'Co-curator of Torano Notte e Giorno', lane: 'works' },
  { year: 2001, title: 'Pinhole Journal Vol. 17 #1 dedicated to him', lane: 'works' },
  { year: 2002, title: 'Senza Obiettivo, Siena — builds 3×3 m camera obscura', lane: 'exhibitions' },
  { year: 2003, title: 'Directs FAI Ferrer monument for Univ. Barcelona (with Nicolazzi)', lane: 'works', hero: true },
  { year: 2006, title: 'Featured in Non son l\'uno per cento (Morabito doc.)', lane: 'works' },
  { year: 2007, title: 'Artempo — Palazzo Fortuny, Venice', lane: 'exhibitions' },
  { year: 2009, title: 'Schwarz Darstellen — Hommage to Jef Verheyen', lane: 'works' },
  { year: 2009, title: 'In-finitum — Palazzo Fortuny, Venice', lane: 'exhibitions' },
  { year: 2010, title: 'Studio renamed Stroobant Dominique → STYLITHOS', lane: 'works' },
  { year: 2011, title: 'TRA. Edge of Becoming — Palazzo Fortuny', lane: 'exhibitions' },
  { year: 2012, title: 'Tempi e Fili — Axel Vervoordt Gallery', lane: 'exhibitions' },
  { year: 2015, title: 'Solo show + monograph (AsaMER, 300 pp.)', lane: 'works', hero: true },
  { year: 2015, title: 'PROPORTIO — Palazzo Fortuny', lane: 'exhibitions' },
  { year: 2016, title: 'Slow Photo Festival — Massa Marittima', lane: 'exhibitions' },
  { year: 2017, title: 'INTUITION — Palazzo Fortuny', lane: 'exhibitions' },
];

export const PEOPLE: Person[] = [
  { id: 'self', name: 'Dominique Stroobant', role: 'sculptor / photographer / Steenkapper', kind: 'self' },
  // teachers
  { id: 'stan-hensen', name: 'Stan Hensen', role: 'teacher at Saint-Luc Brussels', kind: 'collaborator', detail: 'Mentor 1966–70' },
  { id: 'jean-guiraud', name: 'Jean Guiraud', role: 'teacher at Saint-Luc Brussels', kind: 'collaborator', detail: 'Mentor 1966–70' },
  // collaborators / artists
  { id: 'max-bill', name: 'Max Bill', role: 'Swiss artist (1908–1994)', kind: 'collaborator', detail: 'Kontinuität, 1983–86' },
  { id: 'jef-verheyen', name: 'Jef Verheyen', role: 'ZERO artist (Belgium)', kind: 'collaborator', detail: 'Met 1973; marble/granite works' },
  { id: 'paolo-gioli', name: 'Paolo Gioli', role: 'Italian pinhole pioneer', kind: 'collaborator', detail: 'European pinhole track' },
  { id: 'karl-prantl', name: 'Karl Prantl', role: 'Austrian sculptor', kind: 'collaborator', detail: '1976 "Behind those stones" film' },
  { id: 'wout-vercammen', name: 'Wout Vercammen', role: 'Belgian Pop-adjacent artist', kind: 'collaborator', detail: '1975 "World\'s biggest granite stamp"' },
  { id: 'dirk-van-den-eynde', name: 'Dirk Van den Eynde', role: 'filmmaker', kind: 'collaborator', detail: '1976 film co-author' },
  { id: 'nicolazzi', name: 'Alfonso Nicolazzi', role: 'typographer / activist', kind: 'collaborator', detail: '2003 Ferrer monument; 2006 doc.' },
  { id: 'kenneth-davis', name: 'Kenneth Davis', role: 'sculptor', kind: 'group', detail: 'Floating Stones, 1972' },
  { id: 'philippe-toussaint', name: 'Philippe Toussaint', role: 'sculptor', kind: 'group', detail: 'Floating Stones, 1972' },
  { id: 'floating-stones', name: 'Floating Stones Group', role: 'co-founded 1972', kind: 'group' },
  // institutions
  { id: 'fai', name: 'Federazione Anarchica Italiana', role: 'founded Carrara 1945', kind: 'institution' },
  { id: 'fratelli-biselli', name: 'Fratelli Biselli', role: 'granite firm, Carrara', kind: 'institution', detail: 'Since 1976' },
  { id: 'lega-cavatori', name: 'Lega Cavatori di Torano', role: 'quarry workers\' union', kind: 'institution', detail: '1976 film contributors' },
  { id: 'vervoordt', name: 'Axel Vervoordt Gallery', role: 'representation', kind: 'institution' },
  { id: 'pinhole-journal', name: 'Pinhole Journal (Eric Renner)', role: 'pinhole periodical, US', kind: 'institution', detail: 'Vol. 17 #1 (2001) dedicated to DS' },
  // essayists
  { id: 'dirk-porschmann', name: 'Dirk Pörschmann', role: 'essayist', kind: 'collaborator', detail: 'Catalogues 2012 & 2015' },
  { id: 'hendel-teicher', name: 'Hendel Teicher', role: 'co-author', kind: 'collaborator', detail: 'Camera Oscura, 1982' },
  // family
  { id: 'mona', name: 'Mymona "Mona" Johnson', role: 'wife (1973–1998)', kind: 'family', detail: 'South African, Cape Town' },
  { id: 'ish-mael', name: 'Ish-maël Stroobant', role: 'son (b. 1972)', kind: 'family' },
  { id: 'mascha', name: 'Mascha Stroobant', role: 'daughter (b. 1978)', kind: 'family', detail: 'Marine botanist / phycologist' },
  // scholars
  { id: 'alison-leitch', name: 'Alison Leitch', role: 'anthropologist', kind: 'scholar', detail: 'Materiality of Marble (2010)' },
  { id: 'gimpel', name: 'Jean Gimpel', role: 'medieval-tech historian', kind: 'scholar', detail: 'Met 1994' },
  { id: 'mannoni', name: 'Tiziano Mannoni', role: 'archaeologist of materials', kind: 'scholar', detail: 'Met 1994' },
  { id: 'bechmann', name: 'Roland Bechmann', role: 'Villard de Honnecourt scholar', kind: 'scholar', detail: 'Joint exhibitions 2002 & 2004' },
];

export const EDGES: Edge[] = [
  // teachers
  { from: 'self', to: 'stan-hensen', kind: 'collaboration', label: 'taught by, 1966–70' },
  { from: 'self', to: 'jean-guiraud', kind: 'collaboration', label: 'taught by, 1966–70' },
  // collaborators
  { from: 'self', to: 'max-bill', kind: 'collaboration', label: 'Kontinuität, 1983–86' },
  { from: 'self', to: 'jef-verheyen', kind: 'friendship', label: 'met 1973' },
  { from: 'self', to: 'paolo-gioli', kind: 'collaboration', label: 'pinhole pioneers' },
  { from: 'self', to: 'karl-prantl', kind: 'collaboration', label: '1976 film' },
  { from: 'self', to: 'wout-vercammen', kind: 'collaboration', label: '1975 granite stamp' },
  { from: 'self', to: 'dirk-van-den-eynde', kind: 'collaboration', label: '1976 film' },
  { from: 'self', to: 'nicolazzi', kind: 'collaboration', label: '2003 Ferrer monument' },
  // group
  { from: 'self', to: 'floating-stones', kind: 'membership', label: 'co-founder 1972' },
  { from: 'floating-stones', to: 'kenneth-davis', kind: 'membership' },
  { from: 'floating-stones', to: 'philippe-toussaint', kind: 'membership' },
  // institutions
  { from: 'self', to: 'fai', kind: 'membership', label: 'member' },
  { from: 'self', to: 'fratelli-biselli', kind: 'collaboration', label: 'since 1976' },
  { from: 'self', to: 'lega-cavatori', kind: 'collaboration', label: '1976 film' },
  { from: 'self', to: 'vervoordt', kind: 'representation' },
  { from: 'self', to: 'pinhole-journal', kind: 'subject', label: 'Vol. 17 #1 (2001)' },
  // essayists / authors
  { from: 'self', to: 'dirk-porschmann', kind: 'collaboration', label: 'catalogue essays' },
  { from: 'self', to: 'hendel-teicher', kind: 'collaboration', label: 'Camera Oscura, 1982' },
  { from: 'vervoordt', to: 'dirk-porschmann', kind: 'collaboration' },
  // family
  { from: 'self', to: 'mona', kind: 'family', label: 'married 1973' },
  { from: 'self', to: 'ish-mael', kind: 'family', label: 'son' },
  { from: 'self', to: 'mascha', kind: 'family', label: 'daughter — marine botanist' },
  // scholars
  { from: 'self', to: 'alison-leitch', kind: 'subject', label: 'written about by' },
  { from: 'self', to: 'gimpel', kind: 'friendship', label: 'met 1994' },
  { from: 'self', to: 'mannoni', kind: 'friendship', label: 'met 1994' },
  { from: 'self', to: 'bechmann', kind: 'collaboration', label: '2002, 2004' },
  // FAI politics
  { from: 'fai', to: 'nicolazzi', kind: 'membership' },
];

// Numbers reconciled across Fischer 1999 (DMV-Mitteilungen) and welt-der-form.
export const KONTINUITAT_STATS = {
  quarryBlockTons: 500, // raw block in Sardinia per Fischer 1999
  transportTons: 180, // after rough cuts in Sardinia, transported to Carrara
  finalTons: 80,
  carvingYears: 2.5,
  heightMeters: 4.5,
  plannedHeightMeters: 6,
  rawBlockCm: [450, 750, 460] as const,
  granite: 'Rosa Sardo (Rosa Beta)',
  quarry: 'Scarraciana, near Tempio Pausania, Sardinia',
  extracted: '20 October 1983',
  arrivedFrankfurt: '7 September 1986',
  reasonForHeightCut: 'transport-route restrictions from Frankfurt harbour to Taunusanlage',
  topology: 'Möbius-strip endless loop',
  lost1947Version: 'A 3 m plaster-on-steel version stood by Lake Zurich, 1947 — destroyed by vandals soon after.',
  shippingRoute: 'Sardinia → Marina di Carrara → Gibraltar → Rotterdam → Rhine/Main → Frankfurt',
};

// Marcinelle 1956 disaster — sum: 262 dead
export const MARCINELLE: Casualty[] = [
  { country: 'Italy', count: 136, flagEmoji: '🇮🇹' },
  { country: 'Belgium', count: 95, flagEmoji: '🇧🇪' },
  { country: 'Poland', count: 8, flagEmoji: '🇵🇱' },
  { country: 'Greece', count: 6, flagEmoji: '🇬🇷' },
  { country: 'Germany', count: 5, flagEmoji: '🇩🇪' },
  { country: 'France', count: 5, flagEmoji: '🇫🇷' },
  { country: 'Hungary', count: 3, flagEmoji: '🇭🇺' },
  { country: 'England', count: 1, flagEmoji: '🇬🇧' },
  { country: 'Netherlands', count: 1, flagEmoji: '🇳🇱' },
  { country: 'Russia', count: 1, flagEmoji: '🇷🇺' },
  { country: 'Ukraine', count: 1, flagEmoji: '🇺🇦' },
];

export const EXPOSURE_SCALE: ExposureStep[] = [
  { label: '1/250 s', seconds: 1 / 250, iconHint: 'shutter', detail: 'a snapshot' },
  { label: '1 s', seconds: 1, iconHint: 'longshutter' },
  { label: '1 min', seconds: 60, iconHint: 'clock' },
  { label: '1 hour', seconds: 3600, iconHint: 'sundial' },
  { label: '1 day', seconds: 86400, iconHint: 'sunarc-short', detail: 'his first heliograph, 1977' },
  { label: '6 months', seconds: 86400 * 183, iconHint: 'sunarc-long', detail: 'litho film, layered — published 1980' },
];

// Counts recomputed against the Vervoordt-PDF CV. Solo = dedicated solo shows; group = biennials / curated group exhibitions.
export const EXHIBITIONS_BY_DECADE: DecadeBucket[] = [
  { label: '1970s', startYear: 1970, solo: 4, group: 7, highlights: ['First solo, Galleria Penta 1972', 'Venice Biennale 1976', 'Steentuigen 1976', 'Eliofanografie 1979'] },
  { label: '1980s', startYear: 1980, solo: 6, group: 2, highlights: ['Dag in dag uit 1980', 'Hole-o-graphy 1980', '10 m pinhole, Geneva 1982', 'L\'art et le temps 1985'] },
  { label: '1990s', startYear: 1990, solo: 0, group: 3, highlights: ['Pinhole Photography, Houston 1990', 'Arles 1996', 'Bureaux d\'artistes 1995'] },
  { label: '2000s', startYear: 2000, solo: 0, group: 4, highlights: ['Senza Obiettivo 2002', 'Artempo 2007', 'Academia Qui es-tu? 2008', 'In-finitum 2009'] },
  { label: '2010s', startYear: 2010, solo: 2, group: 4, highlights: ['TRA 2011', 'Tempi e Fili 2012', 'Solo 2015 + monograph', 'PROPORTIO 2015', 'INTUITION 2017'] },
  { label: '2020s', startYear: 2020, solo: 0, group: 0, highlights: [] },
];

export const QUOTES: Quote[] = [
  {
    text: 'Everyone at Carrara knows that stone weeps. All of us have experienced how alive stones are.',
    source: 'Stroobant, interview',
    emphasis: 'stone weeps',
  },
  {
    text: 'If art is not a necessity, you don\'t need any art. It must want to get out of you.',
    source: 'Tempi e Fili catalogue, 2012',
    emphasis: 'necessity',
  },
  {
    text: 'Art doesn\'t have an essence. For me, the abstract term "art" is a dream or drive.',
    source: 'Axel Vervoordt artist page',
    emphasis: 'dream or drive',
  },
  {
    text: 'By making stonework — sculpture intended for use — I\'m pushing the limits of sculpture.',
    source: 'Solo show essay, 2015',
    emphasis: 'pushing the limits',
  },
  {
    text: 'I already knew at 13 I would be a stonemason. Stone provided necessary resistance.',
    source: 'Solo show essay, 2015',
    emphasis: 'necessary resistance',
  },
  {
    text: 'A good work for me is like a new word. I struggle over stones and words because what must come is and remains yet-to-be imagined.',
    source: 'Axel Vervoordt artist page',
    emphasis: 'yet-to-be imagined',
  },
];

export const SOURCES = [
  { label: 'Wikipedia — Dominique Stroobant', url: 'https://en.wikipedia.org/wiki/Dominique_Stroobant' },
  { label: 'Wikipedia — Miseglia', url: 'https://en.wikipedia.org/wiki/Miseglia' },
  { label: 'Wikipedia DE — Kontinuität (Skulptur)', url: 'https://de.wikipedia.org/wiki/Kontinuit%C3%A4t_(Skulptur)' },
  { label: 'Welt der Form — Kontinuität', url: 'https://welt-der-form.net/Rekorde/30-Bill-1986-Kontinuitaet.html' },
  { label: 'Axel Vervoordt — Stroobant', url: 'https://www.axel-vervoordt.com/gallery/artists/dominique-stroobant' },
  { label: 'AlternativePhotography — solarigraphy history', url: 'https://www.alternativephotography.com/solarigraphy/' },
  { label: 'Wikipedia — Marcinelle mining disaster', url: 'https://en.wikipedia.org/wiki/Marcinelle_mining_disaster' },
];
